import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, Mic, MessageSquare, Clock, DollarSign, 
  CheckCircle, ArrowRight, Zap, Shield, BarChart3,
  Calendar, ShoppingBag, Settings, Play, Users,
  Building2, CreditCard, Headphones, Code2
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ruonn-replica/artifacts/652j8z6m_logo%20letra%20transparente%20white.png';
const GOOGLE_CALENDAR_LOGO = "https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg";
const SHOPIFY_LOGO = "https://customer-assets.emergentagent.com/job_95fa5d1b-93d8-45fa-8fee-36235fc1312c/artifacts/bni9agb4_shopify-bag-icon-symbol-logo-701751695132537nenecmhs0u.png";

const VoiceAgentPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0b]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="Ruonn" className="h-10 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/#benefits" className="text-gray-400 hover:text-white transition-colors text-sm">
              Why Ruonn
            </Link>
            <Link to="/voice-agent" className="text-purple-400 font-medium text-sm">
              AI Voice Agent
            </Link>
            <Link to="/#testimonials" className="text-gray-400 hover:text-white transition-colors text-sm">
              Testimonials
            </Link>
            <Link to="/#faq" className="text-gray-400 hover:text-white transition-colors text-sm">
              FAQ
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/cloud/login"
              className="flex items-center gap-2 border border-purple-500 text-purple-400 hover:bg-purple-500/10 px-4 py-2 rounded-full text-sm font-medium transition-all"
            >
              <Mic className="w-4 h-4" />
              Login to Cloud
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-2 mb-6">
            <Zap className="w-4 h-4 text-purple-400" />
            <span className="text-purple-400 text-sm font-medium">Ruonn Cloud Platform</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            AI Voice Agent for Your<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
              Business Phone Line
            </span>
          </h1>
          
          <p className="text-xl text-gray-400 mb-8 max-w-3xl mx-auto">
            Transform your customer service with an intelligent AI that answers calls 24/7, 
            schedules appointments, manages orders, and seamlessly transfers to humans when needed.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/cloud/register"
              className="flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 text-white px-8 py-4 rounded-full text-lg font-medium transition-all hover:scale-105"
            >
              <Play className="w-5 h-5" />
              Start Free Trial
            </Link>
            <Link
              to="/cloud/login"
              className="flex items-center justify-center gap-2 border border-gray-600 text-white hover:bg-white/5 px-8 py-4 rounded-full text-lg font-medium transition-all"
            >
              <Headphones className="w-5 h-5" />
              Login to Dashboard
            </Link>
          </div>
          
          <p className="text-gray-500 text-sm mt-4">5 free trial calls • No credit card required</p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent to-purple-900/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Powerful Features for Every Business
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Everything you need to automate your phone communications and delight your customers.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Phone,
                logo: null,
                title: "24/7 Call Answering",
                description: "Never miss a call again. Your AI agent answers every call instantly, any time of day or night."
              },
              {
                icon: MessageSquare,
                logo: null,
                title: "Natural Conversations",
                description: "Powered by advanced AI, your agent holds human-like conversations that customers love."
              },
              {
                icon: null,
                logo: GOOGLE_CALENDAR_LOGO,
                logoAlt: "Google Calendar",
                logoBg: "bg-white/5",
                title: "Appointment Scheduling",
                description: "Integrate with Google Calendar to let customers book, reschedule, or cancel appointments."
              },
              {
                icon: null,
                logo: SHOPIFY_LOGO,
                logoAlt: "Shopify",
                logoBg: "bg-[#96BF48]/10",
                title: "Shopify Integration",
                description: "Check order status, provide tracking info, and handle cancellations for your e-commerce store."
              },
              {
                icon: Users,
                logo: null,
                title: "Call Transfer",
                description: "Seamlessly transfer calls to human operators when needed, with full context preservation."
              },
              {
                icon: BarChart3,
                logo: null,
                title: "Analytics Dashboard",
                description: "Track call volumes, duration, costs, and AI performance with detailed analytics."
              }
            ].map((feature, idx) => (
              <div key={idx} className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 hover:border-purple-500/30 transition-colors group">
                <div className={`w-12 h-12 ${feature.logoBg || 'bg-purple-500/10'} rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110`}>
                  {feature.logo ? (
                    <img src={feature.logo} alt={feature.logoAlt} className="w-8 h-8 object-contain" />
                  ) : (
                    <feature.icon className="w-6 h-6 text-purple-400" />
                  )}
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Integration logos strip */}
          <div className="flex items-center justify-center gap-3 mt-10 flex-wrap">
            <span className="text-gray-500 text-sm">Integrates with</span>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-800 rounded-full px-4 py-1.5">
                <img src={GOOGLE_CALENDAR_LOGO} alt="Google Calendar" className="w-5 h-5" />
                <span className="text-gray-300 text-xs font-medium">Google Calendar</span>
              </div>
              <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-800 rounded-full px-4 py-1.5">
                <img src={SHOPIFY_LOGO} alt="Shopify" className="w-5 h-5 object-contain" />
                <span className="text-gray-300 text-xs font-medium">Shopify</span>
              </div>
              <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-800 rounded-full px-4 py-1.5">
                <Code2 className="w-4 h-4 text-blue-400" />
                <span className="text-gray-300 text-xs font-medium">Custom API</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-6" id="pricing">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Pay only for what you use. No hidden fees, no long-term contracts.
          </p>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Trial */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
              <div className="text-purple-400 font-medium text-sm mb-2">FREE TRIAL</div>
              <div className="text-4xl font-bold text-white mb-2">$0</div>
              <p className="text-gray-400 text-sm mb-6">Get started risk-free</p>
              
              <ul className="space-y-3 mb-8">
                {[
                  "5 free calls",
                  "1 minute max per call",
                  "Basic AI responses",
                  "Dashboard access",
                  "No credit card required"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              
              <Link
                to="/cloud/register"
                className="block w-full py-3 text-center border border-purple-500 text-purple-400 rounded-lg hover:bg-purple-500/10 transition-colors"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Business Profile */}
            <div className="bg-gradient-to-b from-purple-900/30 to-gray-900/50 border border-purple-500/30 rounded-2xl p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-xs px-3 py-1 rounded-full">
                MOST POPULAR
              </div>
              <div className="text-purple-400 font-medium text-sm mb-2">PER BUSINESS PROFILE</div>
              <div className="text-4xl font-bold text-white mb-2">
                $10<span className="text-lg text-gray-400">/mo</span>
              </div>
              <p className="text-gray-400 text-sm mb-6">Per business you manage</p>
              
              <ul className="space-y-3 mb-8">
                {[
                  "Unlimited calls",
                  "10 free minutes included",
                  "Custom AI prompts",
                  "Call transfer support",
                  "Google Calendar integration",
                  "Shopify integration",
                  "Full analytics dashboard",
                  "Priority support"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              
              <Link
                to="/cloud/register"
                className="block w-full py-3 text-center bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
              >
                Get Started
              </Link>
            </div>

            {/* Usage Costs */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
              <div className="text-purple-400 font-medium text-sm mb-2">USAGE COSTS</div>
              <div className="text-4xl font-bold text-white mb-2">Pay as you go</div>
              <p className="text-gray-400 text-sm mb-6">After free minutes</p>
              
              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center py-3 border-b border-gray-800">
                  <span className="text-gray-300">Voice minutes</span>
                  <span className="text-white font-medium">$0.25/min</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-800">
                  <span className="text-gray-300">Phone number</span>
                  <span className="text-white font-medium">$2.00/mo</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-800">
                  <span className="text-gray-300">Free minutes included</span>
                  <span className="text-green-400 font-medium">10 min/profile</span>
                </div>
              </div>
              
              <div className="bg-gray-800/50 rounded-lg p-4 text-sm text-gray-400">
                <strong className="text-white">Example:</strong> A business with 50 minutes of calls pays $10 (subscription) + $10.00 (40 billable min × $0.25) = $20.00/month
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Setup */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent to-purple-900/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Get Started in 5 Minutes
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Setting up your AI Voice Agent is quick and easy. Follow these simple steps.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: "1",
                icon: Users,
                title: "Create Account",
                description: "Sign up for free and get 5 trial calls instantly. No credit card needed."
              },
              {
                step: "2",
                icon: Building2,
                title: "Setup Business Profile",
                description: "Create a profile for your business with custom greeting and AI instructions."
              },
              {
                step: "3",
                icon: Phone,
                title: "Get Phone Number",
                description: "Purchase a dedicated phone number ($2/mo) from our available Twilio numbers."
              },
              {
                step: "4",
                icon: CreditCard,
                title: "Activate Subscription",
                description: "Pay $10/month per business profile to unlock unlimited calls and features."
              }
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 h-full">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm mb-4">
                    {item.step}
                  </div>
                  <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </div>
                {idx < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-purple-500/50" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Configuration Guide */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Configure Your AI Agent
          </h2>
          <p className="text-gray-400 text-center mb-12">
            Customize how your AI agent handles calls with these powerful settings.
          </p>

          <div className="space-y-6">
            {[
              {
                icon: MessageSquare,
                logo: null,
                title: "AI Prompts & Personality",
                description: "Write custom prompts that define how your AI agent responds. Set the tone (friendly, professional, casual) and provide context about your business, services, and common customer questions.",
                example: "Example: 'You are a helpful receptionist for ABC Dental. Our hours are 9am-5pm Mon-Fri. We offer cleanings, fillings, and emergency services.'"
              },
              {
                icon: Settings,
                logo: null,
                title: "Call Transfer Settings",
                description: "Enable call transfer to route calls to a human operator when needed. The AI can recognize when a customer needs human assistance and seamlessly transfer the call with full context.",
                example: "Set a transfer phone number like +1 (555) 123-4567 for urgent inquiries."
              },
              {
                icon: null,
                logo: GOOGLE_CALENDAR_LOGO,
                logoAlt: "Google Calendar",
                logoBg: "bg-white/5",
                title: "Google Calendar Integration",
                description: "Connect your Google Calendar to let the AI check your availability and book appointments directly. Set working hours, buffer times, and appointment durations.",
                example: "Customers can say 'Book me an appointment for tomorrow at 2pm' and the AI handles it."
              },
              {
                icon: null,
                logo: SHOPIFY_LOGO,
                logoAlt: "Shopify",
                logoBg: "bg-[#96BF48]/10",
                title: "Shopify Integration",
                description: "Connect your Shopify store to enable order status checks, tracking information retrieval, and order cancellations via voice commands.",
                example: "Customers can ask 'Where is my order #1234?' and get instant tracking info."
              },
              {
                icon: Code2,
                logo: null,
                logoBg: "bg-blue-500/10",
                iconColor: "text-blue-400",
                title: "Custom API Integrations",
                description: "Connect any external tool or service using our flexible API integration system. Define custom functions that your AI agent can call during conversations.",
                example: "Example: Connect your CRM, booking system, or any REST API to extend your agent's capabilities."
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 hover:border-purple-500/20 transition-colors">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 ${item.logoBg || 'bg-purple-500/10'} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    {item.logo ? (
                      <img src={item.logo} alt={item.logoAlt} className="w-8 h-8 object-contain" />
                    ) : (
                      <item.icon className={`w-6 h-6 ${item.iconColor || 'text-purple-400'}`} />
                    )}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm mb-3">{item.description}</p>
                    <div className="bg-gray-800/50 rounded-lg px-4 py-3 text-sm text-gray-300 italic">
                      {item.example}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-3xl p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Phone Support?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Start with 5 free calls. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/cloud/register"
                className="flex items-center justify-center gap-2 bg-white text-purple-600 px-8 py-4 rounded-full text-lg font-medium hover:bg-gray-100 transition-all"
              >
                <Zap className="w-5 h-5" />
                Start Free Trial
              </Link>
              <Link
                to="/cloud/login"
                className="flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-white/10 transition-all"
              >
                Login to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0b] py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src={LOGO_URL} alt="Ruonn" className="h-8 w-auto mb-4" />
              <p className="text-gray-500 text-sm">
                Expert developers & QA services, plus AI-powered voice solutions for modern businesses.
              </p>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/voice-agent" className="text-gray-400 hover:text-white text-sm">AI Voice Agent</Link></li>
                <li><Link to="/voice-agent#pricing" className="text-gray-400 hover:text-white text-sm">Pricing</Link></li>
                <li><Link to="/#benefits" className="text-gray-400 hover:text-white text-sm">Features</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/#testimonials" className="text-gray-400 hover:text-white text-sm">Testimonials</Link></li>
                <li><Link to="/#faq" className="text-gray-400 hover:text-white text-sm">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Get Started</h4>
              <ul className="space-y-2">
                <li><Link to="/cloud/register" className="text-gray-400 hover:text-white text-sm">Create Account</Link></li>
                <li><Link to="/cloud/login" className="text-gray-400 hover:text-white text-sm">Login to Cloud</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <p className="text-gray-500 text-xs text-center mb-4">
              This site is not a part of the Facebook website or Facebook Inc. Additionally, This site is NOT endorsed by Facebook in any way.
            </p>
            <p className="text-gray-400 text-sm text-center">
              ©2024 ruonn.com. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VoiceAgentPage;
